import { FormLeavingListener } from '@exam-center/components';
import {
  GetExamRoomRequestInfoByIdDocument,
  useCreateOrUpdateExamRoomRequestPhysicalInfoMutation,
  useGetDownloadSignedUrlForExamRoomRequestPhysicalInfoLazyQuery,
  useGetUploadSignedUrlForExamRoomRequestPhysicalInfoLazyQuery,
} from '@exam-center/graphql';
import { Box, Button, Divider, Flex, Form, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { DoorInfoRow, PhotoSetRow, PhysicalInfoFeatureCheckRow } from '../../components';
import { useExamRoomRequestInfo } from '../../providers';

import { AROUND_AREA_FEATURES, PHYSICAL_FEATURES } from './constants';
import {
  examLocationRequestRoomPhysicalInfoFormSchema,
  type ExamLocationRequestRoomPhysicalInfoFormInputs,
} from './schema';

interface ExamLocationRequestRoomPhysicalInfoFormProps {
  isReadonly?: boolean;
  hideSubmit?: boolean;
  submitLabel?: string;
  endAdornment?: React.ReactNode;
  defaultValues?: Partial<ExamLocationRequestRoomPhysicalInfoFormInputs>;
}

export default function ExamLocationRequestRoomPhysicalInfoForm(
  props: ExamLocationRequestRoomPhysicalInfoFormProps,
) {
  const { submitLabel = 'บันทึก', hideSubmit, isReadonly, defaultValues } = props;

  const { showSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();
  const shouldValidate = searchParams.get('validate') === 'true';

  const form = useForm<ExamLocationRequestRoomPhysicalInfoFormInputs>({
    defaultValues: {
      ...(defaultValues ?? {}),
    },
    resolver: shouldValidate
      ? zodResolver(examLocationRequestRoomPhysicalInfoFormSchema)
      : undefined,
  });

  useEffect(() => {
    if (shouldValidate) {
      form.trigger();
    }
  }, [shouldValidate, form]);

  useEffect(() => {
    const frameContent = document.getElementById('frame-content');
    if (frameContent) {
      frameContent.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  const { examRoomRequestInfo } = useExamRoomRequestInfo();

  const [createOrUpdateExamLocationRequestRoomPhysicalInfo, { loading }] =
    useCreateOrUpdateExamRoomRequestPhysicalInfoMutation();

  const onSubmit = (data: ExamLocationRequestRoomPhysicalInfoFormInputs) => {
    createOrUpdateExamLocationRequestRoomPhysicalInfo({
      variables: {
        createOrUpdateExamRoomRequestPhysicalInfoInput: {
          examRoomRequestInfoId: examRoomRequestInfo.id,
          info: data,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'บันทึกข้อมูลลักษณะทางกายภาพเรียบร้อยแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการบันทึกข้อมูลลักษณะทางกายภาพ',
          variant: 'error',
        });
      },
      refetchQueries: [GetExamRoomRequestInfoByIdDocument],
    });
  };

  const [getUploadSignedUrls] = useGetUploadSignedUrlForExamRoomRequestPhysicalInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [getDownloadSignedUrls] = useGetDownloadSignedUrlForExamRoomRequestPhysicalInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });

  function uploadFiles(files: File[]): Promise<string[]> {
    const run = async () => {
      const uploadResult = await getUploadSignedUrls({
        variables: {
          getUploadSignedUrlForExamRoomRequestPhysicalInfoInput: {
            contentTypes: files.map((f) => f.type),
            examRoomRequestInfoId: examRoomRequestInfo.id,
          },
        },
      });

      const signedUploadUrls =
        uploadResult.data?.ecGetUploadSignedUrlForExamRoomRequestPhysicalInfo.signedUrls ?? [];

      if (signedUploadUrls.length < files.length) {
        throw new Error('Not enough signed URLs returned for upload');
      }

      await Promise.all(
        files.map((file, idx) => {
          const signed = signedUploadUrls[idx];
          if (!signed?.url) {
            throw new Error('No matching signed URL found');
          }
          return fetch(signed.url, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });
        }),
      );

      const fileNames = signedUploadUrls
        .map((url) => url.storageObject.fileName ?? '')
        .filter(Boolean);

      return fileNames;
    };

    return run();
  }

  function getImagesDownloadSignedUrl(fileNames: string[]): Promise<string[]> {
    const run = async () => {
      const downloadResult = await getDownloadSignedUrls({
        variables: {
          getDownloadSignedUrlForExamRoomRequestPhysicalInfoInput: {
            fileNames,
            examRoomRequestInfoId: examRoomRequestInfo.id,
          },
        },
      });

      return (
        fileNames?.map((fileName) => {
          return downloadResult.data?.ecGetDownloadSignedUrlForExamRoomRequestPhysicalInfo
            .signedUrls[fileName ?? ''];
        }) ?? []
      );
    };

    return run();
  }

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={form.handleSubmit(onSubmit)}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(8),
          padding: theme.spacing(8),
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(6),
            [theme.breakpoints.down('desktop')]: {
              flexDirection: 'column',
            },
          })}
        >
          <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
            ลักษณะทางกายภาพ
          </Text>
          <Box
            sx={(theme) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(6),
            })}
          >
            {PHYSICAL_FEATURES.map((f) => (
              <PhysicalInfoFeatureCheckRow
                key={f.key}
                config={f}
                isReadonly={isReadonly}
                uploadFiles={uploadFiles}
                getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
              />
            ))}
            <DoorInfoRow
              isReadonly={isReadonly}
              label='8.จำนวนประตูเปิดเข้า-ออกห้องสอบ'
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
              required
            />
            <PhotoSetRow
              name='frontOfExamRoom.images'
              label='9.หน้าห้องสอบ'
              imageInputLabel='แนบรูปภาพบริเวณหน้าห้องสอบ'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
              variant='full'
            />
            <PhotoSetRow
              name='insideExamRoom.images'
              label='10.ภายในห้องสอบเป็นภาพกว้าง'
              imageInputLabel='แนบรูปภาพบริเวณภายในห้องสอบ'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
              variant='full'
            />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(6),
            [theme.breakpoints.down('desktop')]: {
              flexDirection: 'column',
            },
          })}
        >
          <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
            ลักษณะบริเวณโดยรอบของห้องสอบ
          </Text>
          <Box
            sx={(theme) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(6),
            })}
          >
            {AROUND_AREA_FEATURES.map((f) => (
              <PhysicalInfoFeatureCheckRow
                key={f.key}
                config={f}
                isReadonly={isReadonly}
                uploadFiles={uploadFiles}
                getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
              />
            ))}
          </Box>
        </Box>

        {!hideSubmit ? (
          <Flex
            sx={(theme) => ({
              justifyContent: 'flex-end',
              mt: theme.spacing(2),
              gap: theme.spacing(2),
            })}
          >
            <Button
              type='submit'
              disabled={isReadonly || form.formState.isSubmitting || !form.formState.isDirty}
              loading={form.formState.isSubmitting || loading}
            >
              {submitLabel}
            </Button>
          </Flex>
        ) : null}
      </Form>
      {isReadonly ? null : <FormLeavingListener onSubmit={onSubmit} />}
    </FormProvider>
  );
}
